import React, { useState } from 'react';
import { format } from 'date-fns';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import Dialog from './Dialog';
import Button from './Button';
import Form from './Formik/Form';

import { countries } from '@dashboard/services/countries/countries';
import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { getOrderType } from '@dashboard/services/getProductType';

export default function FieldEditDialog({ fields, page, updateFields, trigger }) {
  const [isOpen, setIsOpen] = useState(false);

  const orderType = getOrderType(page);

  return (
    <Dialog
      trigger={
        trigger || (
          <Button
            appearance="light"
            size="small"
            className="ml-2 flex items-center gap-x-2"
            onClick={() => setIsOpen(true)}
          >
            <PencilSquareIcon className="w-4 h-4" /> Edit
          </Button>
        )
      }
      title="Edit fields"
      description={`These fields only update ${orderType} details. To change a customer's name or email, go to the customer's page and edit their details.`}
      open={isOpen}
      onOpenChange={setIsOpen}
      onCancel={() => setIsOpen(false)}
      confirmButton={null}
      cancelButton={null}
    >
      {({ close }) => {
        const formFields = getEditFields(fields, page);

        return (
          <Form
            fields={formFields}
            initialValues={Object.fromEntries(formFields.map(field => [field.name, field._value]))}
            onSubmit={async (values, actions) => {
              try {
                const data = await updateFields(values);

                actions.setStatus('');
                actions.resetForm({
                  values: Object.fromEntries(data.fields.map(field => [field._id, field.value])),
                });
                setIsOpen(false);

                return {
                  data: true,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            cancelButton={{
              label: 'Cancel',
              appearance: 'light',
              onClick: close,
            }}
            formSubmitAlign="end"
          />
        );
      }}
    </Dialog>
  );
}

/**
 * Get fields for the edit dialog
 * @param {Object} payment
 * @returns {Array}
 *  @property {string} type - The type of the field
 *  @property {string} name - The name of the field
 *  @property {Array} options - The options of the field (checkbox, select, multiple-choice, country)
 *  @property {string} label - The label of the field
 *  @property {string} _value - The value of the field from the backend, prefixed with underscore to keep field uncontrolled
 */
function getEditFields(fields, page) {
  return fields.map(field => {
    const pageField = page.fields?.find(f => f._id === field.fieldId);

    const fieldType = getDataFieldTypeLabel(field.type);

    if (pageField) {
      switch (pageField.element) {
        case 'checkbox':
          return {
            type: 'checkbox',
            options: pageField.options,
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'textarea':
          return {
            type: 'textarea',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'select':
          return {
            type: 'select',
            options: pageField.options,
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'multiple-choice':
          return {
            type: 'select',
            options: pageField.options,
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'quantity':
          return {
            type: 'number',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'phone':
          return {
            type: 'tel',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'country':
          return {
            type: 'select',
            options: [
              {
                label: 'Select country',
                value: '',
              },
              ...countries.map(country => ({
                label: country.name,
                value: country.alpha2,
              })),
            ],
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'tax-id':
          return {
            type: 'text',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'email':
          return {
            type: 'email',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'time':
          return {
            type: 'time',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'date-time':
          return {
            type: 'datetime-local',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value ? format(new Date(field.value), 'yyyy-MM-dd HH:mm') : '',
          };
        case 'date':
          return {
            type: 'date',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value ? format(new Date(field.value), 'yyyy-MM-dd') : '',
          };
        case 'po-number':
          return {
            type: 'text',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
        default:
          return {
            type: 'text',
            name: field._id,
            label: field.label,
            labelDescription: fieldType,
            _value: field.value,
          };
      }
    }

    return {
      type: 'text',
      name: field._id,
      label: field.label,
      labelDescription: fieldType,
    };
  });
}
